import { useState, useEffect } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";

// Icons
import { IoGlobe } from "react-icons/io5";

// Mytherra API
import mytherraApi from "services/mytherraApi";

function NetworkSettings() {
  const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:3002' : window.location.origin + '/api';
  const [rpcServer, setRpcServer] = useState(API_BASE);
  const [connectionStatus, setConnectionStatus] = useState("checking");
  const [peers, setPeers] = useState(0);

  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = async () => {
    try {
      const stats = await mytherraApi.getNetworkStatus();
      if (stats) {
        setConnectionStatus("connected");
        setPeers(stats.networkPeers || 0);
      } else {
        setConnectionStatus("disconnected");
      }
    } catch (error) {
      setConnectionStatus("error");
    }
  };

  const getStatusColor = () => {
    switch (connectionStatus) {
      case "connected": return "#00CED1";
      case "disconnected": return "#FF0080";
      case "checking": return "#FFD700";
      default: return "#FF0080";
    }
  };

  const getStatusText = () => {
    switch (connectionStatus) {
      case "connected": return "Connected";
      case "disconnected": return "Disconnected";
      case "checking": return "Checking...";
      default: return "Error";
    }
  };

  return (
    <Card>
      <VuiBox p={3}>
        <VuiBox mb={3}>
          <VuiBox display="flex" alignItems="center" mb={1}>
            <VuiBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              width="48px"
              height="48px"
              borderRadius="12px"
              sx={{ background: "#00CED1" }}
              mr={2}
            >
              <IoGlobe size="24px" color="#000000" />
            </VuiBox>
            <VuiTypography variant="lg" color="white" fontWeight="bold">
              Network Settings
            </VuiTypography>
          </VuiBox>
          <VuiTypography variant="button" color="text" ml={7}>
            Configure blockchain connection
          </VuiTypography>
        </VuiBox>

        {/* Connection Status */}
        <VuiBox mb={3} p={2} sx={{ background: "rgba(255, 255, 255, 0.02)", borderRadius: "8px" }}>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <VuiTypography variant="button" color="text">
              Connection Status
            </VuiTypography>
            <VuiBox display="flex" alignItems="center">
              <Icon sx={{ color: getStatusColor(), fontSize: "8px", mr: 1 }}>
                circle
              </Icon>
              <VuiTypography variant="button" color="white" fontWeight="bold">
                {getStatusText()}
              </VuiTypography>
            </VuiBox>
          </VuiBox>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center">
            <VuiTypography variant="button" color="text">
              Network Peers
            </VuiTypography>
            <VuiTypography variant="button" color="white" fontWeight="bold">
              {peers}
            </VuiTypography>
          </VuiBox>
        </VuiBox>

        {/* RPC Server */}
        <VuiBox mb={2}>
          <VuiTypography variant="caption" color="text" fontWeight="medium" mb={1} display="block">
            RPC Server URL
          </VuiTypography>
          <VuiInput
            placeholder={API_BASE}
            value={rpcServer}
            onChange={(e) => setRpcServer(e.target.value)}
            disabled
          />
        </VuiBox>

        <VuiButton
          variant="outlined"
          color="white"
          fullWidth
          onClick={checkConnection}
          sx={{
            borderColor: "rgba(255, 255, 255, 0.2)",
            mb: 3,
            "&:hover": {
              borderColor: "rgba(255, 255, 255, 0.4)",
            },
          }}
        >
          <Icon sx={{ mr: 1 }}>refresh</Icon>
          Test Connection
        </VuiButton>

        {/* Info Box */}
        <VuiBox p={2} sx={{ background: "rgba(0, 117, 255, 0.1)", borderRadius: "8px" }}>
          <VuiBox display="flex" alignItems="flex-start">
            <Icon sx={{ color: "#0075F6", fontSize: "18px", mr: 1, mt: "2px" }}>info</Icon>
            <VuiBox>
              <VuiTypography variant="caption" color="info" fontWeight="medium">
                Network Information
              </VuiTypography>
              <VuiTypography variant="caption" color="text" display="block" mt={0.5}>
                Connected to Mytherra Blockchain via local node
              </VuiTypography>
            </VuiBox>
          </VuiBox>
        </VuiBox>
      </VuiBox>
    </Card>
  );
}

export default NetworkSettings;
